<template>
  <v-container fluid>
    <page-header :menuItems="menuItems">
      Course Management
    </page-header>
    <v-toolbar elevation="0">
      <v-text-field
        class="mr-md-2"
        prepend-icon="mdi-magnify"
        v-model="search"
        label="Search course name and description"
        single-line
        hide-details
        clearable
        @keyup.enter="keywordSearch"
      ></v-text-field>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-chip
            :color="courseState == 'active' ? 'primary' : ''"
            @click="courseStateSearch('active', true)"
            class="mx-2"
            outlined
            v-on="on"
            >A</v-chip
          >
        </template>
        <span>Show active courses</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-chip
            :color="courseState == 'up-coming' ? 'primary' : ''"
            @click="courseStateSearch('up-coming', true)"
            class="mr-2"
            outlined
            v-on="on"
            >F</v-chip
          >
        </template>
        <span>Show future courses</span>
      </v-tooltip>
      <v-tooltip bottom v-if="!filterExpand">
        <template v-slot:activator="{ on }">
          <v-btn @click="filterExpand = !filterExpand" icon v-on="on">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <span>Search Courses</span>
      </v-tooltip>
      <v-btn @click="filterExpand = !filterExpand" icon v-if="filterExpand">
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </v-toolbar>
    <v-progress-linear indeterminate v-if="isAppLoading" />
    <v-card v-if="filterExpand">
      <v-card-text>
        <v-row>
          <v-col cols="12" lg="4">
            <v-switch
              class="mt-5"
              v-model="openEnrollment"
              label="Open Enrollment Only"
            />
          </v-col>
          <v-col cols="12" lg="4">
            <v-switch
              class="mt-5"
              v-model="includeArchived"
              label="Include Archived"
            />
          </v-col>
          <v-col cols="12" lg="4">
            <v-select
              v-model="filterGrades"
              :items="grades"
              label="Grade"
              return-object
              :clearable="true"
              clear-icon="mdi-close-circle-outline"
              item-value="value"
              item-text="name"
              multiple
            />
          </v-col>
          <v-col cols="12" lg="8">
            <v-select
              v-model="filterTeachers"
              :items="availableTeachers"
              item-text="name"
              item-value="email"
              label="Teachers"
              return-object
              :clearable="true"
              clear-icon="mdi-close-circle-outline"
              multiple
            />
          </v-col>
          <v-col cols="12" lg="4">
            <v-select
              :clearable="true"
              v-model="filterSubject"
              :items="subjects"
              label="Subject"
            />
          </v-col>
          <v-col cols="12" align="center">
            <v-btn @click="loadCourses(true)">Filter Courses</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card elevation="0" class="mt-2">
      <v-card-text>
        <v-data-table
          v-if="courses != null && courses.length > 0"
          :headers="headers"
          :items="courses"
          :items-per-page="cmPage.itemPerPage"
          :server-items-length="cmPage.totalCourses"
          :page="cmPage.pageIndex"
          :disable-sort="true"
          item-key="id"
          class="elevation-1"
          @pagination="pageChanged"
        >
          <template v-slot:item.name="{ item }">
            <router-link
              data-cy="courselink"
              :to="{
                path: '/secure/course/' + item.id,
                params: { id: item.id }
              }"
              >{{ item.name }}</router-link
            >
          </template>

          <template v-slot:item.courseTeachers="{ item }">
            <v-bottom-sheet
              v-if="item.courseTeachers && item.courseTeachers.length > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip outlined v-bind="attrs" v-on="on">
                  {{ item.courseTeachers ? item.courseTeachers.length : 0 }}
                </v-chip>
              </template>
              <v-sheet v-if="item.courseTeachers && item.courseTeachers.length > 0" class="text-center" height="100px">
                <div style="overflow-x: scroll; white-space: nowrap;">
                  <span :key="ct.id" v-for="ct in item.courseTeachers">
                    <v-chip
                      v-if="ct.teacher"
                      :to="`/secure/teacher/${ct.teacher.id}`"
                      class="ma-5"
                      outlined
                      >{{ ct.teacher.firstName }}
                      {{ ct.teacher.lastName }}</v-chip
                    >
                  </span>
                </div>
              </v-sheet>
            </v-bottom-sheet>
          </template>

          <template v-slot:item.courseStudents="{ item }">
            <v-bottom-sheet
              v-if="item.courseStudents && item.courseStudents.length > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip outlined v-bind="attrs" v-on="on">
                  {{ item.courseStudents ? item.courseStudents.length : 0 }}
                </v-chip>
              </template>
              <v-sheet class="text-center" height="100px">
                <div style="overflow-x: scroll; white-space: nowrap;">
                  <span :key="cs.id" v-for="cs in item.courseStudents">
                    <v-chip
                      :to="`/secure/student/${cs.student.id}`"
                      class="ma-5"
                      outlined
                      >{{ cs.student.firstName }}
                      {{ cs.student.lastName }}</v-chip
                    >
                  </span>
                </div>
              </v-sheet>
            </v-bottom-sheet>
          </template>

          <!-- <template
            v-slot:item.courseTopics="{ item }"
          >{{item.courseTopics ? item.courseTopics.length:0}}</template> -->

          <template v-slot:item.schedule="{ item }">
            <span v-html="formatDateRange(item)" />
          </template>

          <template v-slot:item.info="{ item }">
            <v-tooltip v-if="item.description" left>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on"
                  ><v-icon>mdi-information-outline</v-icon></v-btn
                >
              </template>
              <span>
                {{ item.description }}
              </span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
const PageHeader = () => import("@/components/common/PageHeader.vue");

export default {
  components: {
    PageHeader
  },
  data() {
    return {
      menuItems: [
        {
          id: "new-course",
          text: "Add New Course",
          icon: "mdi-database-plus",
          to: { name: "NewCourse" }
        }
      ],
      filterExpand: false,
      availableTeachers: [],
      initLoad: true,
      headers: [
        {
          text: "Course",
          value: "name",
          align: "start",
          sortable: true
        },
        {
          text: "Teachers",
          value: "courseTeachers",
          align: "start",
          sortable: true
        },
        {
          text: "Students",
          value: "courseStudents",
          align: "start",
          sortable: true
        },
        // {
        //   text: "Topics",
        //   value: "courseTopics",
        //   align: "start",
        //   sortable: true
        // },
        { text: "Schedule", value: "schedule", align: "start", sortable: true },
        {
          text: "Status",
          value: "status",
          align: "start",
          sortable: true
        },
        {
          text: "Info",
          value: "info",
          align: "start",
          sortable: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters([
      "navState",
      "isAppLoading",
      "courses",
      "grades",
      "cmPage",
      "cmSearch",
      "cmFilters",
      "cmCourseState",
      "subjects"
    ]),
    search: {
      set(search) {
        this.$store.dispatch("setCMSearch", search);
      },
      get() {
        return this.cmSearch;
      }
    },
    courseState: {
      set(courseState) {
        this.$store.dispatch("setCMCourseState", courseState);
      },
      get() {
        return this.cmCourseState;
      }
    },
    filterGrades: {
      set(filterGrades) {
        this.$store.dispatch("setCMGrades", filterGrades);
      },
      get() {
        return this.cmFilters.grades;
      }
    },
    filterSubject: {
      set(filterSubject) {
        this.$store.dispatch("setCMSubject", filterSubject);
      },
      get() {
        return this.cmFilters.subject;
      }
    },
    filterTeachers: {
      set(filterTeachers) {
        this.$store.dispatch("setCMTeachers", filterTeachers);
      },
      get() {
        return this.cmFilters.teachers;
      }
    },
    openEnrollment: {
      set(openEnrollment) {
        this.$store.dispatch("setCMOpenEnroll", openEnrollment);
      },
      get() {
        return this.cmFilters.openEnroll;
      }
    },
    includeArchived: {
      set(includeArchived) {
        this.$store.dispatch("setCMArchived", includeArchived);
      },
      get() {
        return this.cmFilters.archived;
      }
    }
  },
  methods: {
    ...mapActions(["setCMCourseState", "addErrorMessage"]),
    pageChanged(pagination) {
      this.cmPage.pageIndex = pagination.page;
      this.cmPage.itemPerPage = pagination.itemsPerPage;

      if (!this.initLoad) {
        this.loadCourses();
      }
      this.initLoad = false;
    },
    loadCourses(paramSearch) {
      console.log("running course search...");
      //Clear search term if is param search
      if (paramSearch) {
        this.search = "";
      }
      this.courseState = "";

      const searchStatus = this.openEnrollment ? "Open Enrollment" : null;
      const teacherEmails =
        this.filterTeachers.length > 0 ? this.filterTeachers : null;
      const selectedGrades =
        this.filterGrades.length > 0 ? this.filterGrades : this.grades;
      const selectedSubject = this.filterSubject;
      const searchRequest = {
        filterTerm: {
          COURSE: [
            { name: "status", value: searchStatus },
            { name: "subject", value: selectedSubject },
            {
              name: "grade",
              value: selectedGrades.map(g => g.value).join(",")
            },
            // {name: 'courseState', value: this.includeArchived ? 'ARCHIVED':null },
            {
              name: "courseTeacher.teacher.email",
              value: teacherEmails
                ? teacherEmails.map(t => t.email).join(",")
                : null
            }
          ]
        },
        searchTerm: this.cmSearch,
        searchEntities: ["COURSE"],
        pageSize: this.cmPage.itemPerPage,
        pageIndex: this.cmPage.pageIndex,
        sortInfos: paramSearch ? [{ sortCol: "id", sortDir: "desc" }] : []
      };
      this.$axios
        .post("/classroom/search-courses", searchRequest)
        .then(resp => {
          console.log(resp);
          this.cmPage.totalCourses = resp.data.totalElements;
          this.$store.dispatch("setCourses", resp.data.content);
          this.filterExpand = false;
        })
        .catch(() => {
          this.$store.dispatch(
            "addMessage",
            "Error loading courses from server"
          );
        });
    },
    courseStateSearch(state, initialLoad) {
      if (initialLoad) {
        this.initLoad = true;
      }

      this.courseState = state;
      console.log("running state search for courses");
      this.search = "";
      const params = {
        courseState: state, // available states are 'up-coming', 'active' and 'ended'
        pageSize: this.cmPage.itemPerPage,
        pageIndex: initialLoad ? 1 : this.cmPage.pageIndex
        // sortInfos: [{ sortCol: "id", sortDir: "desc" }]
      };

      this.$axios
        .get("/course/searchCourse", { params })
        .then(resp => {
          const results = resp.data.data.content;
          console.log(results);
          this.cmPage.totalCourses = resp.data.data.totalElements;
          this.$store.dispatch("setCourses", results);
          this.filterExpand = false;
        })
        .catch(error => {
          console.log(error);
          this.addErrorMessage("Error loading active courses from server");
        });
    },
    loadTeachers() {
      return this.$axios.get("/classroom/teachers").then(resp => {
        const teachers = resp.data;
        this.availableTeachers = teachers.map(teacher => {
          return {
            ...teacher,
            name: `${teacher.firstName} ${teacher.lastName.charAt(0)}. (${
              teacher.email
            })`
          };
        });
      });
    },
    formatDateRange(course) {
      if (course.scheduleStart == null && course.scheduleEnd == null) {
        return "N/A";
      } else {
        const start = new moment(course.scheduleStart).format(
          "ddd MMM DD, YYYY"
        );
        const end = course.scheduleEnd
          ? new moment(course.scheduleEnd).format("ddd MMM DD, YYYY")
          : "N/A";
        return `${start} to <br/> ${end}`;
      }
    },
    keywordSearch() {
      this.cmPage.pageIndex = 1;
      this.loadCourses();
    }
  },
  mounted() {
    this.loadTeachers().then(() => {
      if (this.courses.length < 1) {
        this.loadCourses();
      }
    });
  }
};
</script>

<style></style>
